import React, { useEffect, useState } from "react";
import { Save, Calculator } from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "../../../components/common";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { api, errMsg, fmtINR } from "../../../lib/api";
import { pokeLive } from "../../../lib/useLive";
import { VT_KEYS, VehicleTypeBadge, fmtMinutes } from "../../../components/parking/common";

const FIELDS = [
  { key: "first_hour", label: "First hour (or part)" },
  { key: "subsequent_hour", label: "Every further hour (or part)" },
  { key: "day_pass", label: "Day pass" },
  { key: "weekly_pass", label: "Weekly pass" },
  { key: "monthly_pass", label: "Monthly pass" },
];

export const computeFare = (t, minutes) => {
  const h = Math.max(Math.ceil(Math.max(minutes, 1) / 60), 1);
  return Number(t.first_hour || 0) + (h - 1) * Number(t.subsequent_hour || 0);
};

export function TariffsTab({ code, canWrite }) {
  const [rows, setRows] = useState(null);
  const [orig, setOrig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [calc, setCalc] = useState({ vt: "four_wheeler", minutes: 150 });
  const load = () => api.get(`/t/${code}/parking/admin/tariffs`).then((r) => { setRows(r.data.tariffs); setOrig(JSON.stringify(r.data.tariffs)); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = rows && JSON.stringify(rows) !== orig;
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/t/${code}/parking/admin/tariffs`, { tariffs: rows.map((t) => ({ vehicle_type: t.vehicle_type, ...Object.fromEntries(FIELDS.map((f) => [f.key, Number(t[f.key])])) })) });
      setRows(r.data.tariffs); setOrig(JSON.stringify(r.data.tariffs));
      toast.success("Tariffs updated — new bookings use the new rates");
      pokeLive(code, "admin");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  if (!rows) return <Spinner />;
  const cur = rows.find((t) => t.vehicle_type === calc.vt) || rows[0];
  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4" data-testid="parking-tariffs">
      <Card className="shadow-sm xl:col-span-2">
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <div><CardTitle className="text-base font-heading">Tariff card (₹)</CardTitle><p className="text-xs text-muted-foreground mt-1">Hourly fare = first hour + every further started hour. Passes are flat for the period.</p></div>
          {canWrite && <Button onClick={save} disabled={!dirty || saving} data-testid="tariffs-save-button"><Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save tariffs"}</Button>}
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Vehicle class</TableHead>{FIELDS.map((f) => <TableHead key={f.key} className="text-right">{f.label}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.vehicle_type} data-testid={`tariff-row-${t.vehicle_type}`}>
                  <TableCell><VehicleTypeBadge type={t.vehicle_type} /></TableCell>
                  {FIELDS.map((f) => (
                    <TableCell key={f.key} className="text-right">
                      <Input type="number" min="0" step="1" value={t[f.key]} disabled={!canWrite} className="h-8 w-28 ml-auto text-right tabular-nums" data-testid={`tariff-${t.vehicle_type}-${f.key}`}
                        onChange={(e) => setRows(rows.map((x) => (x.vehicle_type === t.vehicle_type ? { ...x, [f.key]: e.target.value } : x)))} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {dirty && <p className="text-xs text-[hsl(var(--warning))] mt-2" data-testid="tariffs-dirty-note">Unsaved changes</p>}
        </CardContent>
      </Card>
      <Card className="shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-base font-heading flex items-center gap-2"><Calculator className="h-4 w-4 text-primary" /> Fare preview</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1"><Label>Vehicle class</Label>
            <Select value={calc.vt} onValueChange={(v) => setCalc({ ...calc, vt: v })}><SelectTrigger data-testid="fare-calc-vehicle"><SelectValue /></SelectTrigger>
              <SelectContent>{VT_KEYS.map((k) => <SelectItem key={k} value={k}>{rows.find((t) => t.vehicle_type === k)?.label || k}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1"><Label>Duration (minutes)</Label><Input type="number" min="15" step="15" value={calc.minutes} onChange={(e) => setCalc({ ...calc, minutes: Number(e.target.value) })} data-testid="fare-calc-minutes" /></div>
          <div className="rounded-lg bg-accent/50 p-3">
            <p className="text-xs text-muted-foreground">{fmtMinutes(calc.minutes)} → {Math.max(Math.ceil(calc.minutes / 60), 1)} billable hour(s)</p>
            <p className="text-2xl font-semibold font-heading tabular-nums" data-testid="fare-calc-result">{fmtINR(computeFare(cur, calc.minutes))}</p>
            <p className="text-[11px] text-muted-foreground">{fmtINR(cur.first_hour)} first hour + {Math.max(Math.ceil(calc.minutes / 60), 1) - 1} × {fmtINR(cur.subsequent_hour)}</p>
          </div>
          <p className="text-[11px] text-muted-foreground">Uses the values currently on screen (saved or not), so you can preview a change before saving.</p>
        </CardContent>
      </Card>
    </div>
  );
}
